import { AxiosResponse } from 'axios';
import { API } from '../../utils/axios';
import { APIResponseStatus } from '../types';
import {
  GetQuestionsResponse,
  JoinQuestionerParam as JoinQuestionnaireParam,
  JoinQuestionnaireResponse,
  SubmitQuestionFile,
} from '../../features/get-care/Components/select-visit-reason/types';
import {
  FileUploadResponse,
  GetQuestionnaireResponse,
  SubmitQuestionnaireResponse,
  SubmitQuestionResponse,
} from '../../features/get-care/Components/get-care/types';

const handleFileUpdate = async (
  file: SubmitQuestionFile,
  questionnaireResponseId: string,
  linkId: string,
) => {
  let fileUploaded = false;
  let uploadedFileResource: FileUploadResponse | undefined = undefined;

  if (!file.uploaded) {
    uploadedFileResource = (await uploadFile(file, questionnaireResponseId, linkId)).resource;
    if (uploadedFileResource) fileUploaded = true;
  }

  const resource = uploadedFileResource?.entry[0].resource;

  return {
    ...(fileUploaded && {
      answer: [
        {
          valueAttachment: {
            contentType: resource?.blobdata?.contentType,
            url: resource?.url,
            size: resource?.blobdata?.contentLength,
            title: resource?.name,
          },
        },
      ],
    }),
  };
};

const uploadFile = async (
  file: SubmitQuestionFile,
  questionnaireResponseId: string,
  linkId: string,
) => {
  const formData = new FormData();
  formData.append('resourceId', questionnaireResponseId);
  formData.append('resourceTag', linkId);
  formData.append('file', file.file);
  return API.post<any, AxiosResponse<APIResponseStatus<FileUploadResponse>>>(
    `/v1/resources/File`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  ).then((res) => res.data);
};

class QuestionnaireService {
  static joinQuestionnaire(params: JoinQuestionnaireParam) {
    return API.post<any, AxiosResponse<APIResponseStatus<JoinQuestionnaireResponse>>>(
      `/v1/resources/QuestionnaireResponse`,
      params,
    ).then((res) => res.data);
  }

  static getQuestions(params: { questionnaireResponseId: string; linkId?: string; op?: string }) {
    const { questionnaireResponseId, ...queryParams } = params;
    const queryString = new URLSearchParams(queryParams).toString();
    return API.get<any, AxiosResponse<APIResponseStatus<GetQuestionsResponse>>>(
      `/v1/resources/QuestionnaireResponse/${
        questionnaireResponseId
      }/Question${queryString ? '?' + queryString : ''}`,
    ).then((res) => res.data);
  }

  static submitQuestion = async ({
    params,
    questionnaireResponseId,
  }: {
    params: any;
    questionnaireResponseId: string;
  }) => {
    const { file, ...bodyParams } = params;
    let fileUpdated = null;

    // TODO: Need to do after file upload implementation
    // Upload file related to questioner
    if (params?.file) {
      const fileParam: SubmitQuestionFile = {
        file: file.value,
        type: file.type,
        fileName: file.fileName,
      };
      fileUpdated = await handleFileUpdate(
        fileParam,
        questionnaireResponseId,
        params.resource.linkId,
      );
    }

    // Submit Questioner
    return API.post<any, AxiosResponse<APIResponseStatus<SubmitQuestionResponse>>>(
      `/v1/resources/QuestionnaireResponse/${questionnaireResponseId}/Item`,
      {
        ...bodyParams,
        ...(!!fileUpdated && {
          resource: {
            ...bodyParams?.resource,
            ...fileUpdated,
          },
        }),
      },
    ).then((res) => res.data);
  };

  static getQuestionnaire = (questionnaireResponseUuid: string) => {
    return API.get<any, AxiosResponse<APIResponseStatus<GetQuestionnaireResponse>>>(
      `/v1/resources/QuestionnaireResponse/${questionnaireResponseUuid}`,
    ).then((res) => res.data.resource);
  };

  static submitQuestionnaire = async (questionnaireResponseUuid: string) => {
    const questionnaire = await this.getQuestionnaire(questionnaireResponseUuid);

    const submitParams = {
      context: {
        intent: 'answer questionnaire',
      },
      resource: {
        ...questionnaire,
        status: 'completed',
      },
    };

    return API.post<any, AxiosResponse<APIResponseStatus<SubmitQuestionnaireResponse>>>(
      `/v1/resources/QuestionnaireResponse`,
      submitParams,
    ).then((res) => res.data);
  };
}

export default QuestionnaireService;
